import { createRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@trackercop/contracts';
import { rootRoute } from './__root';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { buttonClasses } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CrawlStatusBadge } from '@/components/crawl-status-badge';
import { CrawlFilterBar } from '@/components/crawl-filter-bar';
import { ScriptsTab } from '@/components/crawl-tabs/scripts-tab';
import { CookiesTab } from '@/components/crawl-tabs/cookies-tab';
import { PayloadsTab } from '@/components/crawl-tabs/payloads-tab';
import { GtmTab } from '@/components/crawl-tabs/gtm-tab';
import { ConsentTab } from '@/components/crawl-tabs/consent-tab';
import { ComplianceTab } from '@/components/crawl-tabs/compliance-tab';
import { FindingsTab } from '@/components/crawl-tabs/findings-tab';
import { formatDateTime } from '@/lib/format';

type ConsentMode = components['schemas']['ConsentMode'];
type FindingReview = components['schemas']['FindingReview'];

interface CrawlSearch {
  mode?: ConsentMode | 'all';
  gtmContainerId?: string;
  tab?: string;
  destinationDomain?: string;
  vendorId?: string;
}

export const crawlDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crawls/$crawlId',
  component: CrawlResultsPage,
  validateSearch: (search: Record<string, unknown>): CrawlSearch => ({
    mode: search.mode as ConsentMode | 'all' | undefined,
    gtmContainerId: typeof search.gtmContainerId === 'string' ? search.gtmContainerId : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    destinationDomain:
      typeof search.destinationDomain === 'string' ? search.destinationDomain : undefined,
    vendorId: typeof search.vendorId === 'string' ? search.vendorId : undefined,
  }),
});

function CrawlResultsPage() {
  const { crawlId } = crawlDetailRoute.useParams();
  const search = crawlDetailRoute.useSearch();
  const navigate = crawlDetailRoute.useNavigate();

  const consentMode: ConsentMode | 'all' = search.mode ?? 'all';
  const gtmContainerId = search.gtmContainerId ?? 'all';
  const tab = search.tab ?? 'scripts';

  const crawlQuery = useQuery({
    queryKey: queryKeys.crawls.detail(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}', { params: { path: { crawlId } } });
      if (error) throw error;
      return data;
    },
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status === 'running' || status === 'pending' ? 2000 : false;
    },
  });

  // Site lookup for the page title — the crawl row alone tells us
  // siteId but not baseUrl, so we fetch the site to render
  // "Crawl https://example.edu" instead of "Crawl cd5807c3…". Skipped
  // for harness scans (the harness URL lives on the crawl row already)
  // and while the crawl is still loading.
  const siteId = crawlQuery.data?.siteId;
  const isHarness = crawlQuery.data?.isHarness ?? false;
  const siteQuery = useQuery({
    queryKey: queryKeys.sites.detail(siteId ?? ''),
    enabled: Boolean(siteId) && !isHarness,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/sites/{siteId}', {
        params: { path: { siteId: siteId! } },
      });
      if (error) throw error;
      return data;
    },
  });
  const headerLabel = (() => {
    const harnessUrl = crawlQuery.data?.harnessBaseUrl;
    if (harnessUrl) return harnessUrl;
    const siteUrl = siteQuery.data?.site?.baseUrl;
    if (siteUrl) return siteUrl;
    return `${crawlId.slice(0, 8)}…`;
  })();

  const containersQuery = useQuery({
    queryKey: queryKeys.crawls.gtmContainers(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/gtm/containers', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const reviewsQuery = useQuery({
    queryKey: queryKeys.crawls.reviews(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/reviews', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const reviews = useMemo(() => {
    const map = new Map<string, FindingReview>();
    for (const r of reviewsQuery.data?.items ?? []) {
      map.set(`${r.entityType}:${r.entityId}`, r);
    }
    return map;
  }, [reviewsQuery.data]);

  function setSearch(next: Partial<CrawlSearch>) {
    navigate({
      search: (prev) => ({ ...prev, ...next }),
      replace: true,
    });
  }

  function selectContainer(id: string) {
    setSearch({ gtmContainerId: id, tab: 'scripts' });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-2xl font-semibold break-all"
            data-testid="crawl-detail-title"
          >
            Crawl {headerLabel}
          </h1>
          {crawlQuery.data && (
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <CrawlStatusBadge status={crawlQuery.data.status} />
              <span>Started {formatDateTime(crawlQuery.data.startedAt)}</span>
              <span>·</span>
              <span>{crawlQuery.data.pagesCrawled} pages</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/crawls/$crawlId/graph"
            params={{ crawlId }}
            className={buttonClasses({ size: 'sm', variant: 'outline' })}
            data-testid="crawl-lineage-link"
          >
            Lineage graph
          </Link>
          <Link
            to="/crawls/$crawlId/data-sharing"
            params={{ crawlId }}
            search={consentMode === 'all' ? {} : { mode: consentMode }}
            className={buttonClasses({ size: 'sm', variant: 'outline' })}
            data-testid="crawl-data-sharing-link"
          >
            Data Sharing map
          </Link>
        </div>
      </div>

      <CrawlFilterBar
        consentMode={consentMode}
        onConsentModeChange={(mode) => setSearch({ mode })}
        gtmContainers={containersQuery.data?.items ?? []}
        gtmContainerId={gtmContainerId}
        onGtmContainerChange={(id) => setSearch({ gtmContainerId: id })}
      />

      <Tabs value={tab} onValueChange={(t) => setSearch({ tab: t })}>
        <TabsList>
          <TabsTrigger value="scripts">Scripts</TabsTrigger>
          <TabsTrigger value="cookies">Cookies</TabsTrigger>
          <TabsTrigger value="payloads">Payloads</TabsTrigger>
          <TabsTrigger value="gtm">GTM</TabsTrigger>
          <TabsTrigger value="consent">Consent</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="findings">Findings</TabsTrigger>
        </TabsList>
        <TabsContent value="scripts">
          <ScriptsTab crawlId={crawlId} consentMode={consentMode} gtmContainerId={gtmContainerId} reviews={reviews} />
        </TabsContent>
        <TabsContent value="cookies">
          <CookiesTab crawlId={crawlId} consentMode={consentMode} gtmContainerId={gtmContainerId} reviews={reviews} />
        </TabsContent>
        <TabsContent value="payloads">
          <PayloadsTab
            crawlId={crawlId}
            consentMode={consentMode}
            gtmContainerId={gtmContainerId}
            reviews={reviews}
            destinationDomain={search.destinationDomain ?? ''}
            onDestinationDomainChange={(v) =>
              setSearch({ destinationDomain: v === '' ? undefined : v })
            }
          />
        </TabsContent>
        <TabsContent value="gtm">
          <GtmTab
            crawlId={crawlId}
            containers={containersQuery.data?.items ?? []}
            onContainerSelected={selectContainer}
            reviews={reviews}
          />
        </TabsContent>
        <TabsContent value="consent">
          <ConsentTab crawlId={crawlId} />
        </TabsContent>
        <TabsContent value="compliance">
          <ComplianceTab crawlId={crawlId} />
        </TabsContent>
        <TabsContent value="findings">
          <FindingsTab crawlId={crawlId} reviews={reviews} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
